import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Loading } from '../../global/Loading/Loading'
import { useSessionLanguage } from '../../global/context/SessionSettingsContext'
import { useSnackbar } from '../../global/context/SnackbarContext'
import { PageQuery, useApiClient } from '../../utils/ApiClient'
import { useGAEvents } from '../../utils/callGAEvents'
import { ClientApiClient } from '../../utils/clientApi'
import { useCallbackWithForceRefresh } from '../../utils/useCallbackWithForceRefresh'
import { useFetchAppendablePage } from '../../utils/useFetch'
import { useScrollToTop } from '../../utils/useScrollToTop'
import { useIntroducingBrokerById } from '../hooks/useIntroducingBrokerById'
import { Loyalty } from './Loyalty'

export const LoyaltyPage: FC = () => {
  useScrollToTop()
  const { addSnackbar } = useSnackbar()
  const { callGAEvents } = useGAEvents()
  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { data: introducingBroker, isLoading, forceRefresh } = useIntroducingBrokerById()

  const { callback: loyaltyRewardsHistoryCb, forceRefresh: forceRefreshRewardHistory } =
    useCallbackWithForceRefresh(
      async (query?: PageQuery) => {
        if (introducingBroker?.id) {
          return apiClient.getIBLoyaltyRewardsHistory(introducingBroker.id, {
            ...query,
            caller: 'ca',
            languageId: locale,
          })
        }
        return Promise.resolve({
          items: [],
          itemsCount: 0,
          pageIndex: 0,
          pageSize: 0,
          totalPages: 0,
          totalResults: 0,
        })
      },
      [apiClient, introducingBroker, locale]
    )

  const query = useFetchAppendablePage(loyaltyRewardsHistoryCb)

  const onSubmitReward = async ({
    rewardId,
    rewardTierLabel,
  }: {
    rewardId: string
    rewardTierLabel?: string | null
  }) => {
    await apiClient.consumeLoyaltyReward(rewardId)

    callGAEvents().IBDashboard.LoyaltyProgram.claimRewards()
    addSnackbar.success({
      message: rewardTierLabel
        ? t('IB.Your reward for the {{levelTitle}} was successfully redeemed', {
            levelTitle: rewardTierLabel,
          })
        : t('IB.Your reward was successfully redeemed'),
    })

    forceRefresh()
    forceRefreshRewardHistory()
  }

  return (
    <Loading showLoadingIcon isLoading={isLoading}>
      <Loyalty
        loyaltyTiers={introducingBroker?.loyaltyDetails}
        introducingBroker={introducingBroker}
        rewardsHistoryQuery={query}
        onSubmitReward={onSubmitReward}
      />
    </Loading>
  )
}
